import React, { useMemo, useState } from "react";
import { Route, Switch, useParams, useRouteMatch } from "react-router-dom";
import { Button, Card, FullscreenTakeover, QueryLoader } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { MISSING_VALUE_PLACEHOLDER } from "../../components/common/constants";
import { DefaultModal } from "../../components/common/DefaultModal";
import { JointAccountDetails } from "../../components/details/JointAccountDetails";
import { DocumentDetailsInvestor } from "../../components/documents/DocumentDetailsInvestor";
import { InstitutionEditScreen } from "../../components/InstitutionEditScreen";
import { DocumentExpiryCompletionCard } from "../../components/landingscreens/DocumentExpiryCompletionCard";
import { DocumentExpiryLandingCard } from "../../components/landingscreens/DocumentExpiryLandingCard";
import { ProfileActionOverview } from "../../components/ProfileActionOverview";
import { ProfileTree } from "../../components/ProfileTree";
import { provideLegalName } from "../../components/ProfileTree/utils";
import {
  AccountTypeEnum,
  ComplianceCaseAction,
  ComplianceCaseAuditTagType,
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
  ComplianceObjectType,
} from "../../graphql/supergraphEnums";
import {
  usePerformActionMutation,
  useGetUserComplianceCasesQuery,
} from "../../graphql/supergraphHooks";
import { addDays } from "../../utils/date_formatter";
import { clientContext } from "../../utils/graphql_utils";
import { ComplianceCaseContext } from "../ComplianceCaseContext";
import {
  contentWrapper,
  navigationButton,
} from "../ComplianceRefreshComponent/styles_vanilla.css";
import { findRefreshUIState } from "../ComplianceRefreshComponent/utils";
import { buildComponentContext } from "../StandAloneCompliance/utils";
import { LandingUIState } from "../types";

export type ComplianceDocumentExpiryComponentProps = {
  arenaId: number;
  errorComponent: React.JSX.Element;
};

export const ComplianceDocumentExpiry: React.FC<ComplianceDocumentExpiryComponentProps> = ({
  arenaId,
  errorComponent,
}) => {
  const { profileGuid } = useParams<{ profileGuid: string; profileId?: string }>();
  const { url: basePath } = useRouteMatch();

  const [showPartialSubmissionModal, setShowPartialSubmissionModal] = useState(false);

  const result = useGetUserComplianceCasesQuery({
    context: clientContext(),
    fetchPolicy: "no-cache",
    variables: {
      caseType: ComplianceCaseTypeEnum.DocumentRefresh,
      externalObject: {
        objectTypeEnum: ComplianceExternalObjectType.ComplianceProfile,
        objectId: profileGuid,
        service: "main",
      },
      auditTag: ComplianceCaseAuditTagType.CaseSubmitted,
    },
  });

  const [performAction, { loading: performActionLoading }] = usePerformActionMutation({
    context: clientContext(),
    refetchQueries: ["GetUserComplianceCases"],
    awaitRefetchQueries: true,
    onCompleted: () => {
      // Removing the keys stored for showing the details in the document refresh case
      Object.keys(localStorage)
        .filter((key) => key.startsWith("jsq_details_aml_"))
        .forEach((key) => localStorage.removeItem(key));
    },
  });

  const complianceCase = result.data?.userAccessibleComplianceCases[0];
  const profileName = useMemo(
    () => provideLegalName(complianceCase?.investingProfile),
    [complianceCase?.investingProfile],
  );
  const dueDate = useMemo(() => {
    return addDays(complianceCase?.createdAt, 30);
  }, [complianceCase?.createdAt]);
  const isIndividual = useMemo(() => {
    return (
      complianceCase?.investingProfile?.accountType === AccountTypeEnum.Individual ||
      complianceCase?.investingProfile?.complianceObjectType === ComplianceObjectType.Individual
    );
  }, [complianceCase?.investingProfile]);

  const componentContext = buildComponentContext(arenaId, basePath, true, {
    id: complianceCase?.id,
    caseType: complianceCase?.caseType,
    investingProfileId: complianceCase?.investingProfile?.id,
    programId: complianceCase?.programId,
  });

  const documentExpiryUIState = useMemo(() => {
    return findRefreshUIState(performActionLoading, complianceCase);
  }, [complianceCase, performActionLoading]);

  const goToPortalHome = () => {
    window.location.href = "/lp/home";
  };

  const handleExitClick = () => {
    if (documentExpiryUIState === LandingUIState.Compliance) {
      setShowPartialSubmissionModal(true);
    } else {
      goToPortalHome();
    }
  };

  const handleContinue = (startSubmission: boolean = false) => {
    void performAction({
      variables: {
        caseId: Number(complianceCase?.id),
        action: startSubmission
          ? ComplianceCaseAction.StartSubmission
          : ComplianceCaseAction.CompleteSubmission,
      },
    });
  };

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header
        leftActionContent={
          <Button
            variant="neutral"
            icon="xmark"
            iconPosition="left"
            iconVariant="outline"
            className={navigationButton}
            onClick={handleExitClick}
          >
            Exit
          </Button>
        }
        title={
          profileName !== MISSING_VALUE_PLACEHOLDER
            ? `${profileName} Document Refresh`
            : MISSING_VALUE_PLACEHOLDER
        }
        rightActionContent={
          <Icon
            type="question-circle"
            variant="outline"
            className={navigationButton}
            onClick={() => {
              window.open("https://investor-help.junipersquare.com/hc/en-us", "_blank");
            }}
          />
        }
      />
      <QueryLoader result={result} renderError={() => errorComponent}>
        <FullscreenTakeover.Body>
          {documentExpiryUIState === LandingUIState.CaseNotFound ? (
            errorComponent
          ) : (
            <Card className={contentWrapper}>
              {/* Landing screen */}
              {documentExpiryUIState === LandingUIState.Confirmation && (
                <DocumentExpiryLandingCard
                  profileName={profileName}
                  dueDate={dueDate ?? "--"}
                  onContinue={handleContinue}
                  onSkip={goToPortalHome}
                  isLoading={performActionLoading}
                />
              )}

              {/* Completion screen */}
              {documentExpiryUIState === LandingUIState.Completion && (
                <DocumentExpiryCompletionCard
                  profileName={profileName}
                  onClose={goToPortalHome}
                />
              )}

              {/* Profile Tree screen */}
              {documentExpiryUIState === LandingUIState.Compliance && (
                <ComplianceCaseContext.Provider value={componentContext}>
                  <Switch>
                    <Route path={`${basePath}`} exact>
                      {isIndividual ? (
                        <ProfileActionOverview onStepCompletion={() => handleContinue()} />
                      ) : (
                        <ProfileTree onStepCompletion={() => handleContinue()} />
                      )}
                    </Route>
                    <Route path={`${basePath}/overview/:profileId`} exact>
                      <ProfileActionOverview onStepCompletion={() => handleContinue()} />
                    </Route>
                    <Route path={`${basePath}/documents/:profileId`} exact>
                      <DocumentDetailsInvestor onStepCompletion={() => handleContinue()} />
                    </Route>
                    <Route path={`${basePath}/edit-institution/:profileId`} exact>
                      <InstitutionEditScreen />
                    </Route>
                    <Route path={`${basePath}/edit-joint/:profileId`} exact>
                      <JointAccountDetails />
                    </Route>
                  </Switch>
                </ComplianceCaseContext.Provider>
              )}
            </Card>
          )}
        </FullscreenTakeover.Body>
      </QueryLoader>
      <DefaultModal
        show={showPartialSubmissionModal}
        title="Warning"
        body="Are you sure you want to exit to portal? Any unsaved information will be lost."
        posBtnText="Continue"
        posBtnVariant="primary"
        negBtnText="Cancel"
        onClose={() => setShowPartialSubmissionModal(false)}
        onConfirm={() => {
          setShowPartialSubmissionModal(false);
          goToPortalHome();
        }}
      />
    </FullscreenTakeover>
  );
};
